import React from 'react';
import { AlertTriangleIcon, ArrowRightIcon, LightbulbIcon } from 'lucide-react';
import { ScreenProps } from '../../types/ptp';
import { PrimaryButton } from '../ui/PrimaryButton';
import { SecondaryButton } from '../ui/SecondaryButton';
export function Screen14_QualityScore({
  data,
  onNext
}: ScreenProps) {
  const steps = data.stepHazardData || [];
  const positiveItems = [{
    text: 'The task sequence was explained clearly',
    pos: true
  }, ...(steps.length > 0 ? [{
    text: `${steps[0].description} was discussed in detail`,
    pos: true
  }] : []), ...(steps.length > 1 ? [{
    text: `${steps[1].description} was covered`,
    pos: true
  }] : [])];
  const negativeItems = [{
    text: 'Emergency response procedures were not clearly reviewed',
    pos: false
  }, {
    text: 'Only some crew members verbally participated',
    pos: false
  }, {
    text: 'PPE confirmation was not completed for all crew members',
    pos: false
  }, ...(steps.length > 3 ? [{
    text: `${steps[steps.length - 1].description} was not discussed in detail`,
    pos: false
  }] : [])];
  const allItems = [...positiveItems, ...negativeItems];
  return <div className="flex flex-col h-full" data-id="element-1034">
      <div className="mb-6" data-id="element-1035">
        <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-1036">
          Meeting Quality Score
        </h2>
        <p className="text-gray-500" data-id="element-1037">AI evaluation of the safety briefing.</p>
      </div>

      <div className="flex-1 overflow-y-auto pb-6 -mx-2 px-2 space-y-6" data-id="element-1038">
        <div className="bg-white border border-gray-200 rounded-xl p-6 flex flex-col items-center text-center relative overflow-hidden" data-id="element-1039">
          <div className="absolute top-0 w-full h-1 bg-amber-500" data-id="element-1040" />
          <div className="text-6xl font-black text-gray-900 mb-2 tracking-tighter" data-id="element-1041">
            72<span className="text-3xl text-gray-400" data-id="element-1042">/100</span>
          </div>
          <div className="inline-flex items-center gap-2 bg-amber-50 text-amber-600 px-3 py-1 rounded-full text-sm font-bold uppercase tracking-wider" data-id="element-1043">
            <AlertTriangleIcon className="w-4 h-4" data-id="element-1044" />
            Needs Improvement
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-5" data-id="element-1045">
          <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4" data-id="element-1046">
            Score Breakdown
          </h3>
          <div className="space-y-4" data-id="element-1047">
            {[{
            label: 'Task scope clarity',
            score: '16 / 20',
            pct: 80,
            color: 'bg-green-500'
          }, {
            label: 'Hazard coverage',
            score: '14 / 20',
            pct: 70,
            color: 'bg-amber-500'
          }, {
            label: 'Control measures reviewed',
            score: '16 / 20',
            pct: 80,
            color: 'bg-green-500'
          }, {
            label: 'Crew participation',
            score: '10 / 20',
            pct: 50,
            color: 'bg-red-500'
          }, {
            label: 'Confirmation of critical conditions',
            score: '16 / 20',
            pct: 80,
            color: 'bg-green-500'
          }].map((item, i) => <div key={i} data-id="element-1048">
                <div className="flex justify-between text-sm mb-1" data-id="element-1049">
                  <span className="text-gray-700" data-id="element-1050">{item.label}</span>
                  <span className="text-gray-900 font-medium" data-id="element-1051">
                    {item.score}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-1.5" data-id="element-1052">
                  <div className={`${item.color} h-1.5 rounded-full`} style={{
                width: `${item.pct}%`
              }} data-id="element-1053" />
                </div>
              </div>)}
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-5" data-id="element-1054">
          <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-4" data-id="element-1055">
            Why this score?
          </h3>
          <ul className="space-y-3 text-sm" data-id="element-1056">
            {allItems.map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-1057">
                <div className={`w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0 ${item.pos ? 'bg-green-500' : 'bg-red-500'}`} data-id="element-1058" />
                <span className={item.pos ? 'text-gray-700' : 'text-red-600'} data-id="element-1059">
                  {item.text}
                </span>
              </li>)}
          </ul>
        </div>

        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-5" data-id="element-1060">
          <div className="flex items-center gap-2 mb-4" data-id="element-1061">
            <LightbulbIcon className="w-5 h-5 text-yellow-500" data-id="element-1062" />
            <h3 className="text-sm font-bold text-yellow-600 uppercase tracking-wider" data-id="element-1063">
              How to improve the next meeting
            </h3>
          </div>
          <ul className="space-y-3" data-id="element-1064">
            {['Ask each crew member to confirm their role and responsibilities', 'Review environmental conditions and stop-work triggers', 'Confirm what to do if unexpected hazards arise', 'Have the full crew confirm required PPE', 'Ask each crew member to repeat the main risks in their own words'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-1065">
                <ArrowRightIcon className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" data-id="element-1066" />
                <span className="text-yellow-700 text-sm leading-snug" data-id="element-1067">
                  {item}
                </span>
              </li>)}
          </ul>
        </div>
      </div>

      <div className="sticky bottom-0 z-10 pt-3 pb-2 space-y-3 bg-gray-50" data-id="element-1068">
        <PrimaryButton onClick={onNext} data-id="element-1069">Review PTP Changes</PrimaryButton>
        <SecondaryButton onClick={() => {}} data-id="element-1070">Done</SecondaryButton>
      </div>
    </div>;
}